<template>
    <Dialog v-model:open="opened">
        <DialogTrigger>
            <slot />
        </DialogTrigger>
        <DialogContent class="max-w-2xl">
            <DialogHeader>
                <template #icon>
                    <Globe class="text-z-primary-600" stroke-width="1.75" />
                </template>

                <DialogTitle>
                    {{ $t("page_domains.dns_setup.title") }}
                </DialogTitle>
                <DialogDescription>
                    {{ $t("page_domains.dns_setup.description") }}
                </DialogDescription>
            </DialogHeader>

            <div class="space-y-4 p-6">
                <!-- Domain Badge -->
                <div class="gap-2 flex flex-wrap items-center">
                    <Badge variant="secondary" class="font-mono">
                        {{ domain }}
                    </Badge>
                    <Badge v-if="isVerified" variant="success">
                        <Check :size="12" class="mr-1" />
                        {{ $t("page_domains.dns_setup.active") }}
                    </Badge>
                    <Badge
                        v-else
                        variant="outline"
                        class="border-amber-300 bg-amber-50 text-amber-600"
                    >
                        <Clock :size="12" class="mr-1" />
                        {{ $t("page_domains.dns_setup.pending") }}
                    </Badge>
                    <Badge
                        v-if="cfSsl?.status"
                        variant="outline"
                        :class="sslStatusBadgeClasses"
                    >
                        <Shield :size="12" class="mr-1" />
                        SSL: {{ sslStatusText }}
                    </Badge>
                </div>

                <!-- DNS Records Card -->
                <div
                    class="rounded-xl border-iron-200 bg-iron-50 overflow-hidden border"
                >
                    <!-- Header -->
                    <div
                        class="border-iron-200 bg-white px-4 py-3 flex items-center justify-between border-b"
                    >
                        <div class="gap-2 flex items-center">
                            <Terminal :size="16" class="text-iron-500" />
                            <span class="text-sm font-medium text-iron-700">
                                {{ $t("page_domains.dns_setup.dns_records") }}
                            </span>
                        </div>
                    </div>

                    <!-- Records -->
                    <div class="divide-iron-200 divide-y">
                        <!-- CNAME Record -->
                        <DnsRecordRow
                            type="CNAME"
                            type-color="blue"
                            :label="
                                $t('page_domains.dns_setup.cname_description')
                            "
                            :name="domain"
                            value="cname.usezaptime.com"
                            :copied-field="copiedField"
                            :validation="dnsValidation?.cname"
                            @copy="copyToClipboard"
                        />

                        <!-- Hostname Pre-validation TXT Record -->
                        <DnsRecordRow
                            v-if="
                                cfOwnershipVerification?.name &&
                                cfOwnershipVerification?.value
                            "
                            type="TXT"
                            type-color="purple"
                            :label="
                                $t(
                                    'page_domains.dns_setup.txt_prevalidation_description',
                                )
                            "
                            :name="cfOwnershipVerification.name"
                            :value="cfOwnershipVerification.value"
                            :copied-field="copiedField"
                            :validation="dnsValidation?.txt"
                            @copy="copyToClipboard"
                        />
                    </div>
                </div>

                <div>
                    <Button
                        as="a"
                        :href="
                            route('app.page_domains.downloads.dns', {
                                pageDomain: pageDomainId,
                            })
                        "
                        variant="ghost"
                    >
                        {{ $t("domains.download") }}
                    </Button>
                </div>

                <!-- Status Info -->
                <div
                    v-if="!isVerified"
                    class="gap-3 rounded-lg border-amber-200 bg-amber-50 p-3 flex items-start border"
                >
                    <Info
                        :size="16"
                        class="mt-0.5 text-amber-600 flex-shrink-0"
                    />
                    <div class="text-sm text-amber-800">
                        <p>
                            {{
                                $t(
                                    "page_domains.dns_setup.help.propagation_time",
                                )
                            }}
                        </p>
                        <p class="mt-1">
                            {{ $t("page_domains.dns_setup.help.ssl_auto") }}
                        </p>
                    </div>
                </div>

                <div v-if="cfStatus" class="text-xs text-iron-500">
                    {{ $t("page_domains.dns_setup.cf_status") }}:
                    <span class="font-medium">{{ cfStatus }}</span>
                    <span v-if="cfSsl?.certificate_authority" class="ml-2">
                        ({{ cfSsl.certificate_authority }})
                    </span>
                </div>
            </div>

            <DialogFooter>
                <Button variant="outline" @click="opened = false">
                    {{ $t("close") }}
                </Button>
                <Button
                    v-if="!isVerified"
                    :disabled="loading"
                    @click="handleVerify"
                >
                    <RefreshCw
                        v-if="loading"
                        :size="16"
                        :stroke-width="1.5"
                        class="mr-2 animate-spin"
                    />
                    {{ $t("page_domains.verify") }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog-v2";
import {
    Globe,
    RefreshCw,
    Check,
    Clock,
    Terminal,
    Info,
    Shield,
} from "lucide-vue-next";
import { ref, computed } from "vue";
import route from "ziggy-js";
import { useI18n } from "vue-i18n";
import { usePageDomains } from "@/composables/usePageDomains";
import DnsRecordRow from "./DnsRecordRow.vue";
import type {
    CfOwnershipVerification,
    CfSsl,
    DnsValidation,
} from "@/types/PageDomains";

const props = defineProps<{
    pageDomainId: number;
    domain: string;
    verifiedAt: string | null;
    cfStatus: string | null;
    cfSsl?: CfSsl | null;
    cfOwnershipVerification?: CfOwnershipVerification | null;
    dnsValidation?: DnsValidation | null;
}>();

const { t } = useI18n();
const opened = ref(false);
const loading = ref(false);
const copiedField = ref<string | null>(null);

const { verifyPageDomain } = usePageDomains();

const isVerified = computed(() => props.verifiedAt !== null);

const sslStatusText = computed(() => {
    const status = props.cfSsl?.status;
    if (!status) return "";
    const key = `page_domains.dns_setup.ssl_status.${status}`;
    const translated = t(key);
    // If translation doesn't exist, fall back to unknown
    return translated === key
        ? t("page_domains.dns_setup.ssl_status.unknown")
        : translated;
});

const sslStatusBadgeClasses = computed(() => {
    const status = props.cfSsl?.status;
    if (status === "active") {
        return "border-green-300 bg-green-50 text-green-600";
    }
    if (status === "pending_validation" || status === "pending_issuance") {
        return "border-blue-300 bg-blue-50 text-blue-600";
    }
    return "border-iron-300 bg-iron-50 text-iron-600";
});

const handleVerify = () => {
    loading.value = true;
    verifyPageDomain(props.pageDomainId, {
        onFinish: () => {
            loading.value = false;
        },
    });
};

const copyToClipboard = async (text: string, field: string) => {
    try {
        await navigator.clipboard.writeText(text);
        copiedField.value = field;
        setTimeout(() => {
            copiedField.value = null;
        }, 2000);
    } catch {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        copiedField.value = field;
        setTimeout(() => {
            copiedField.value = null;
        }, 2000);
    }
};
</script>
